import React, { useContext, useEffect, useState } from "react"
import GroupedReservationLinkField from "../Reservations/EditReservationForm/GroupedReservationLinkField"
import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  MapPinIcon,
  PencilIcon,
} from "@heroicons/react/24/solid"
import classNames from "../../../utils/classNames"
import { AdminContext } from "../../../contexts/AdminContext"
import IGroupedReservation from "../../../interfaces/IGroupedReservation"
import api from "../../../api2"
import Modal from "../../ui/Modal"
import moment from "moment"
import { formatMoney, formatMoneyFromCents } from "../../../utils/formatMoney"
import { CampingStyleProps, ReservationProps } from "../../../interfaces"
import { StatusBlocked, StatusDeleted, StatusImported, StatusPaid, StatusUnpaid } from "../../ui/StatusBadges"
import { LONG_TERM_RESERVATION_KINDS, longTermReservationKind, RESERVATION_KINDS, STATUSES } from "../../../constants"
import pluralize from "../../../utils/pluralize"
import Button from "../../ui/Button"
import { GROUP_PAYMENT_TYPES } from "../../../constants/group_payment_types"
import { PriceDetails as IPriceDetail } from "../../../utils/priceDetailsFor"
import mergePriceDetails from "../../../utils/mergePriceDetails"
import NumberInput from "../../ui/NumberInput"
import useAdminForm from "../../../hooks/useAdminForm2"
import { formatName } from "../../../utils/formatName"
import ReservationDecorator from "../../../decorators/ReservationDecorator"
import { ModalContext } from "../../../contexts/admin/ModalContext"
import stripeIntentUrlFor from "../../../utils/admin/stripeIntentUrlFor"
import { currencySymbol } from "~/utils/currencySymbol"
import { formatDateRange } from "~/utils/formatDateRange"
import { DocumentIcon, PlusIcon } from "@heroicons/react/20/solid"
import CamperCard from "~/components/ui/CamperCard"
import ProfileCard from "../Campers/Camper/ProfileCard"
import DropdownButton from "~/components/ui/Admin/DropdownButton"
import IInvoice from "~/interfaces/IInvoice"
import copyText from "~/utils/copyText"
import ReservationInvoiceWrapper from "~/components/helpers/ReservationInvoiceWrapper"
import EditInvoice from "../Invoices/EditInvoice"
import { INVOICE_STATUSES } from "~/decorators/InvoiceDecorator"
import ILongTermReservation from "~/interfaces/ILongTermReservation"
import { isInclusivelyBeforeDay, SingleDatePicker } from "react-dates"
import parseDateTime from "~/utils/parseDateTime"
import useLongTermReservation from "./hooks/useLongTermReservation"
import { useWatch } from "react-hook-form"
import Spinner from "~/components/ui/Spinner"
import Badge from "~/components/ui/Admin/Badge"
import { useDebouncedCallback } from "use-debounce"
import { once } from "lodash"
import HoverTip from "~/components/ui/HoverTip"

interface IForm {
  amountOwedByGroup: number
  amountPaidByGroup: number
}

interface ComponentProps {
  reservation: ReservationProps
}

const LongTermReservationModalContent = ({ reservation }: ComponentProps) => {
  const {
    reservationStates,
    camp,
    sites,
    showPaymentModal,
    updateLocalReservations,
    showModal,
    setCurrentPage,
    setSelectedReservation,
    closeModal,
    campingStyles,
    showNotification,
  } = useContext(AdminContext)
  const { onClose } = useContext(ModalContext)

  const {
    resource: longTermReservation,
    update: updateLongTermReservation,
    destroy: destroyLongTermReservation,
    isLoading,
    createFinalInvoice,
  } = useLongTermReservation({ id: reservation.long_term_reservation_id, campId: camp.id })
  const [isRemoveButtonClicked, setIsRemoveButtonClicked] = useState<boolean>(false)
  const [reservations, setReservations] = useState<ReservationProps[]>([])
  const [priceDetail, setPriceDetail] = useState<IPriceDetail>(null)
  const [extensionDate, setExtensionDate] = useState<moment.Moment>(
    parseDateTime(reservations[reservations.length - 1]?.end_date, camp?.timezone, {
      asMoment: true,
      initializeOnInvalid: true,
    }),
  )
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false)
  const [isFocusStart, setIsFocusStart] = useState<boolean>(false)
  const [isExtending, setIsExtending] = useState<boolean>(false)
  const [isFocusEnd, setIsFocusEnd] = useState<boolean>(false)
  const [amountOwed, setAmountOwed] = useState<number>(0)
  const [amountPaid, setAmountPaid] = useState<number>(0)
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false)

  const [isPaid, setIsPaid] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const [savedEndDate, setSavedEndDate] = useState<moment.Moment | null>(null)
  const [hasConflicts, setHasConflicts] = useState<boolean>(false)

  // TODO: we should have this in react-query
  const fetchRecurrences = useDebouncedCallback(async () => {
    api.Reservations.recurrencesFor(reservation.id)
      .then((res) => {
        if (res.recurrences.length === 0) {
          onClose()
          return
        }
        setReservations(res.recurrences)
        setAmountOwed(res.recurrences.reduce((acc, r) => acc + r.total_owed_in_cents, 0))
        setAmountPaid(res.recurrences.reduce((acc, r) => acc + r.total_paid_in_cents, 0))
        setIsPaid(amountPaid >= amountOwed)
        if (!savedEndDate && res.recurrences.length > 0) {
          const last = res.recurrences[res.recurrences.length - 1]
          setSavedEndDate(parseDateTime(last.end_date, camp?.timezone, { asMoment: true }))
          setExtensionDate(parseDateTime(last.end_date, camp?.timezone, { asMoment: true }))
        }
      })
      .catch((err) => {
        console.error(err)
        onClose()
      })
      .finally(() => {
        setIsTableLoading(false)
      })
  }, 100)

  const debouncedSaveRecurrences = useDebouncedCallback(() => {
    setIsSaving(true)
    api.Reservations.saveRecurrences(reservation.id, {
      recurrences: reservations,
    })
      .then((res) => {
        setIsExtending(false)
        updateLocalReservations(res.reservations)
        fetchRecurrences()
        showNotification({
          type: "success",
          title: "Success",
          description: "Long-term reservations saved",
        })
      })
      .catch((err) => {
        const serverMessage = err?.errors[0] || "An unexpected error occurred"
        showNotification({
          type: "error",
          title: "Error",
          description: serverMessage,
        })
      })
      .finally(() => {
        setIsSaving(false)
      })
  }, 600)

  useEffect(() => {
    if (!open || !reservation?.id) return

    setIsTableLoading(true)
    fetchRecurrences()
  }, [open, reservation?.id])

  useEffect(() => {
    if (reservations.length > 0) {
      const lastEndDate = parseDateTime(reservations[reservations.length - 1].end_date, camp?.timezone, {
        asMoment: true,
        initializeOnInvalid: true,
      })
      setExtensionDate(lastEndDate)
    }
  }, [reservations])

  const onClickEditInvoice = (reservation: ReservationProps) => {
    showModal(
      <ReservationInvoiceWrapper reservation={reservation} order={reservation.order}>
        {({ orderInvoice }) => (
          <EditInvoice
            invoice={orderInvoice}
            site={reservation.site}
            campingStyle={(campingStyles as CampingStyleProps[]).find(
              (cs) => cs.id === reservation?.site?.camping_style_id,
            )}
            onSuccess={(invoice) => closeModal()}
            isHidingBackButton
          />
        )}
      </ReservationInvoiceWrapper>,
      true,
      "max-w-7xl",
    )
  }

  const dropdownOptionsFor = (reservation: ReservationProps) => {
    const deletedAt = reservation?.deleted_at

    const options = [
      {
        label: "Edit invoice",
        onClick: () => {
          onClickEditInvoice(reservation)
        },
      },
      {
        label: "Copy invoice link",
        onClick: () => {
          copyText(window.location.origin + `/i/${reservation.hashed_id}`)
        },
      },
      {
        label: "View reservation",
        onClick: () => {
          setCurrentPage("reservations")
          setSelectedReservation(reservation)
          closeModal()
        },
      },
    ]

    if (deletedAt === null && reservation.status !== STATUSES.PAID) {
      options.splice(4, 0, {
        label: "Take payment now",
        onClick: () => {
          // TODO: payment modal shouldn't expect reservation, but order
          showPaymentModal(reservation.order?.unpaid_total_in_cents, { reservation: reservation })
        },
      })
    }

    if (reservation.status !== STATUSES.PAID) {
      options.splice(4, 0, {
        label: "Mark as paid",
        onClick: () => {
          api.Reservations.update(reservation?.id, {
            status: "paid",
            lock_version: reservation?.lock_version,
          })
            .then(({ reservation }) => {
              setReservations((prev) => prev.map((res) => (res.id === reservation.id ? reservation : res)))

              updateLocalReservations([reservation])
              // TODO: refresh local invoices so that it updates on the FE
              // props.refreshLocalInvoices()
              showNotification({
                type: "success",
                title: "Success",
                description: "Marked invoice as paid",
              })
            })
            .catch((err) => {
              showNotification({
                type: "error",
                title: "Error",
                description: err.message,
              })
            })
        },
      })
    }

    return options
  }

  const getRecurrenceType = (kind: longTermReservationKind | "seasonal" | "final_invoice") => {
    switch (kind) {
      case "seasonal":
        return <Badge color="gray">Seasonal</Badge>
      case "final_invoice":
        return <Badge color="gray">Final invoice</Badge>
      case LONG_TERM_RESERVATION_KINDS.RENT_AND_UTILITIES:
        return <Badge color="gray">Rent & utilities</Badge>
      case LONG_TERM_RESERVATION_KINDS.UTILITIES_ONLY:
        return <Badge color="gray">Utilities only</Badge>
      default:
        return <Badge color="gray">Rent only</Badge>
    }
  }

  const generateFinalInvoice = () => {
    createFinalInvoice(undefined, {
      onSuccess: () => {
        setIsTableLoading(true)
        fetchRecurrences()
      },
    })
  }

  // TODO: hook this up to BE logic
  const extendLongTermReservation = () => {
    setIsFocusEnd(true)
  }

  useEffect(() => {
    setIsPaid(reservations.every((r) => r.status === STATUSES.PAID))
  }, [reservations.map((r) => r.status).join("")])

  useEffect(() => {
    setIsRemoveButtonClicked(false)
  }, [open])

  const handleCancelClick = () => {
    onClose()
  }

  const handleRemoveClick = () => {
    !isRemoveButtonClicked ? setIsRemoveButtonClicked(true) : handleRemove()
  }

  const handleRemove = () => {
    setIsRemoveButtonClicked(false)
    destroyLongTermReservation(undefined, {
      onSuccess: (response) => {
        const deletedReservations = response.data
        updateLocalReservations(deletedReservations)
        setReservations(deletedReservations)
        setSelectedReservation((prev) => deletedReservations.find((res) => res.id === prev.id) || prev)
        onClose()
      },
      onError: (err) => {
        showNotification({
          type: "error",
          title: "Error",
          description: err.message,
        })
      },
    })
  }

  return (
    <>
      <div className="mb-4 w-full">
        <div className="flex items-center justify-between mb-4 bg-gray-100 px-4 pt-6 pb-4 -m-6 rounded-t-lg">
          <div className="flex flex-col w-full">
            <div className="font-semibold text-xl">
              Long term reservation for {formatName(reservation?.camper)}
            </div>
            <div className="flex items-center mt-2">
              <div className="flex items-center text-gray-600">
                <DocumentTextIcon className="w-5 h-5 text-gray-600 mr-2" />
                <div className="font-medium">
                  {reservations.length} {pluralize("invoice", reservations.length)}
                </div>
              </div>
              <div className="flex items-center text-gray-600 ml-4 sm:ml-8">
                <CalendarDaysIcon className="w-5 h-5 text-gray-600 mr-2" />
                <div className="">
                  <span className="font-medium">
                    {reservations.length > 0 &&
                      `${parseDateTime(reservations[0]?.start_date, camp?.timezone, { asMoment: true, initializeOnInvalid: true }).format("MMM Do")} - ${parseDateTime(
                        reservations[reservations.length - 1]?.end_date,
                        camp?.timezone,
                        { asMoment: true, initializeOnInvalid: true },
                      ).format("MMM Do")}`}
                  </span>
                </div>
              </div>
              <div className="ml-4 sm:ml-8 font-medium">
                {longTermReservation?.deleted_at ? (
                  <StatusDeleted deleted_at={longTermReservation?.deleted_at} />
                ) : isPaid ? (
                  <StatusPaid />
                ) : (
                  <StatusUnpaid />
                )}
              </div>
            </div>
            <div className="flex justify-between mt-3.5 text-sm text-gray-500">
              <span className="mr-4 italic">
                Created{" "}
                {parseDateTime(reservation?.created_at, camp?.timezone, {
                  asMoment: true,
                  initializeOnInvalid: true,
                }).format("M/D/YYYY h:mma")}
              </span>
              <span className="italic">
                Last edited{" "}
                {parseDateTime(reservation?.updated_at, camp?.timezone, {
                  asMoment: true,
                  initializeOnInvalid: true,
                }).fromNow()}
              </span>
            </div>
          </div>
        </div>
        <>
          {/* Parent container */}
          <div className="flex items-stretch justify-between mt-4 mb-4">
            <ProfileCard camper={reservation?.camper} hideAdditionalInfo hidePaymentMethods />

            {/* Child container */}
            <div className="border border-gray-200 rounded-md px-4 py-auto flex items-center gap-8 self-stretch">
              <div className="flex flex-col w-full">
                <div className="text-gray-400 font-semibold text-sm whitespace-nowrap">Total Owed</div>
                <div className="text-lgxl font-medium text-black">
                  {formatMoneyFromCents(
                    reservations.reduce((acc, r) => acc + r.total_owed_in_cents, 0),
                    camp?.currency,
                  )}
                </div>
              </div>
              <div className="flex flex-col w-full">
                <div className="text-gray-400 font-semibold text-sm">Total Paid</div>
                <div className="text-lgxl font-medium text-black">
                  {formatMoneyFromCents(
                    reservations.reduce((acc, r) => acc + r.total_paid_in_cents, 0),
                    camp?.currency,
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      </div>

      <div className="flex mb-2 gap-4">
        <div className="flex-1 grow">
          <div>
            <label htmlFor="name" className="block font-semibold text-md text-gray-900">
              Check-in
            </label>
          </div>

          <div className={"datepicker-disabled-styles"}>
            <SingleDatePicker
              noBorder
              id="start_date"
              block
              date={parseDateTime(reservations[0]?.start_date, camp?.timezone, {
                asMoment: true,
                initializeOnInvalid: true,
              })}
              onDateChange={(date) => { }}
              isOutsideRange={(day) => false}
              focused={isFocusStart}
              onFocusChange={(e) => setIsFocusStart(e.focused)}
              numberOfMonths={1}
              hideKeyboardShortcutsPanel={true}
              displayFormat={camp.date_format}
              disabled={true}
              readOnly={true}
            />
          </div>
        </div>

        <div className="flex-1 grow">
          <div>
            <div className={"flex justify-between items-center"}>
              <label htmlFor="name" className="block font-semibold text-md text-gray-900">
                Check-out
              </label>
              <span
                className={classNames(
                  "ml-3.5 mb-0.5 inline underline text-xs text-gray-500 cursor-pointer hover:text-gray-900",
                )}
                onClick={() => {
                  setExtensionDate(savedEndDate)
                  setIsExtending(false)
                  setIsTableLoading(true)
                  fetchRecurrences()
                }}
              >
                Reset date
              </span>
            </div>
            <div className={"datepicker-disabled-styles"}>
              <SingleDatePicker
                noBorder
                id="end_date"
                block
                date={extensionDate}
                onDateChange={(date) => {
                  setIsTableLoading(true)
                  setExtensionDate(date)
                  api.Reservations.simulateExtension(reservation.id, date)
                    .then((res) => {
                      const processedRecurrences = res.recurrences.map((r) => ({
                        ...r,
                        error_message: r.error_message || null,
                      }))

                      const hasConflicts = processedRecurrences.some((r) => r.error_message)

                      setReservations(processedRecurrences)
                      setAmountOwed(processedRecurrences.reduce((acc, r) => acc + r.total_owed_in_cents, 0))
                      setAmountPaid(processedRecurrences.reduce((acc, r) => acc + r.total_paid_in_cents, 0))
                      setIsPaid(amountPaid >= amountOwed)
                      setIsExtending(true)
                      setHasConflicts(hasConflicts)
                    })
                    .catch((err) => {
                      console.error(err)
                    })
                    .finally(() => {
                      setIsTableLoading(false)
                    })
                }}
                focused={isFocusEnd}
                onFocusChange={(e) => {
                  setIsFocusEnd(e.focused)
                }}
                numberOfMonths={1}
                initialVisibleMonth={() => extensionDate || savedEndDate || moment()}
                hideKeyboardShortcutsPanel={true}
                isOutsideRange={(day) => {
                  return savedEndDate ? !day.isAfter(savedEndDate, "day") : true
                }}
                displayFormat={camp.date_format}
                disabled={isTableLoading || isLoading}
                readOnly={isTableLoading || isLoading}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end">
        <HoverTip
          active={!!reservations?.find((res) => res.kind === RESERVATION_KINDS.FINAL_INVOICE)}
          content="You cannot extend the reservation if there is already a final invoice."
        >
          <button
            type="button"
            className="mt-2.5 py-1 pl-2 flex items-center justify-center w-52 text-blue-500 cursor-pointer hover:bg-blue-50 rounded-full disabled:opacity-30 disabled:pointer-events-none"
            disabled={isLoading || !!reservations?.find((res) => res.kind === RESERVATION_KINDS.FINAL_INVOICE)}
            onClick={() => {
              if (!isLoading) {
                extendLongTermReservation()
              }
            }}
          >
            <PlusIcon className="w-5 h-5" />
            <span className="ml-0.5 mb-0.25 text-md font-semibold underline text-center">Extend reservation</span>
          </button>
        </HoverTip>
      </div>

      <>
        <div className="font-semibold mb-2 pt-2">Invoices</div>
        <div className="flex flex-col">
          <div className="-my-2 overflow-x-auto sm:overflow-x-hidden sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-4 lg:px-8">
              <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
                {!isTableLoading ? (
                  <table className="min-w-full w-full table-auto divide-y divide-gray-200 overflow-x-auto">
                    <>
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-[22.5px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Dates
                          </th>
                          <th
                            scope="col"
                            className="px-[22.5px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Type
                          </th>
                          <th
                            scope="col"
                            className="px-[22.5px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Owed
                          </th>
                          <th
                            scope="col"
                            className="px-[22.5px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Paid
                          </th>
                          <th
                            scope="col"
                            className="px-[22.5px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Status
                          </th>
                          <th
                            scope="col"
                            className="px-[22.5px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          ></th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reservations
                          .sort((a, b) => {
                            // If a.kind is null (aka is a Seasonal), put it first
                            if (a.kind === null && b.kind !== null) return -1
                            if (a.kind !== null && b.kind === null) return 1
                            // Then sort by start_date
                            return new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
                          })
                          .map((res) => {
                            if (!res) return <></>
                            if (res.error_message) {
                              return (
                                <tr key={res.camper?.email + res.start_date}>
                                  <td colSpan={6} className="pl-5 pr-4 py-4 text-sm text-red-600 italic bg-red-50">
                                    {res.error_message}
                                  </td>
                                </tr>
                              )
                            }

                            return (
                              <tr key={res.camper?.email}>
                                <td className="pl-5 pr-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <div className="font-medium text-gray-900">
                                    <span>{formatDateRange(res.start_date, res.end_date)}</span>
                                  </div>
                                </td>
                                <td className="px-[22.5px] py-3 whitespace-nowrap text-sm text-gray-500">
                                  <span>
                                    {getRecurrenceType(
                                      res?.kind == null
                                        ? "seasonal"
                                        : res?.kind == RESERVATION_KINDS.FINAL_INVOICE
                                          ? "final_invoice"
                                          : longTermReservation?.kind,
                                    )}
                                  </span>
                                </td>
                                <td className="text-right">
                                  {formatMoney(res.total_owed_in_cents / 100, camp?.currency)}
                                </td>
                                <td className="text-right">
                                  {res.status === "draft"
                                    ? ""
                                    : formatMoney(res.total_paid_in_cents / 100, camp?.currency)}
                                </td>
                                <td className="px-[22.5px] py-3 whitespace-nowrap text-sm text-gray-500">
                                  <span>
                                    {res?.status === "draft" ? (
                                      <Badge color="gray">DRAFT</Badge>
                                    ) : res?.deleted_at ? (
                                      <StatusDeleted
                                        deleted_at={parseDateTime(res?.deleted_at, camp?.timezone, {
                                          asMoment: true,
                                        }).fromNow()}
                                      />
                                    ) : res?.status === STATUSES.UNPAID ? (
                                      <StatusUnpaid />
                                    ) : res?.status === STATUSES.PAID ? (
                                      <StatusPaid refundedAmountInCents={res?.order?.amount_refunded_in_cents} />
                                    ) : [STATUSES.IMPORTED, "blocked-import"].includes(res?.status) ? (
                                      <StatusImported />
                                    ) : (
                                      <StatusBlocked />
                                    )}
                                  </span>
                                </td>
                                {res.status !== "draft" && (
                                  <td className="px-2 whitespace-nowrap text-sm text-gray-500">
                                    <DropdownButton isMenuFixed stopPropagation options={dropdownOptionsFor(res)}>
                                      <EllipsisHorizontalIcon className="rounded-md -mb-2 p-2 w-10 h-10 transition-colors duration-100 hover:bg-gray-100 text-gray-600 cursor-pointer" />
                                    </DropdownButton>
                                  </td>
                                )}
                              </tr>
                            )
                          })}
                      </tbody>
                    </>
                  </table>
                ) : (
                  <div className="flex justify-center items-center w-full h-48">
                    <Spinner size={12} />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end">
            {!reservations?.find((res) => res.kind === RESERVATION_KINDS.FINAL_INVOICE) && (
              <button
                type="button"
                className="mt-2.5 py-1 pl-2 flex items-center justify-center w-52 text-blue-500 cursor-pointer hover:bg-blue-50 rounded-full disabled:opacity-30 disabled:pointer-events-none"
                disabled={isLoading}
                onClick={() => {
                  if (!isLoading) {
                    generateFinalInvoice()
                  }
                }}
              >
                <PlusIcon className="w-5 h-5" />
                <span className="ml-0.5 mb-0.25 text-md font-semibold underline text-center">
                  Generate final invoice
                </span>
              </button>
            )}
          </div>
        </div>
      </>
      <div className="flex flex-shrink-0 justify-between items-center mt-8 mb-2">
        <div className="flex items-center">
          <Button
            variant="red"
            type="button"
            disabled={isLoading}
            onClick={handleRemoveClick}
            style={{ animationDuration: "1s !important" }}
            className="mr-auto"
          >
            {!isRemoveButtonClicked ? "Remove" : "Are you sure?"}
          </Button>
          {isRemoveButtonClicked && (
            <div className="hidden sm:block ml-3 text-red-500 text-sm transition-all duration-300 w-32 leading-5">
              This will remove all recurrences!
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Button
            type="button"
            variant="gray"
            className={"w-28"}
            onClick={handleCancelClick}
            dataTestId="cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="green"
            className={classNames("w-28 mr-4", !isExtending && "pointer-events-none opacity-30")}
            disabled={!isExtending || hasConflicts || isSaving}
            dataTestId="save-button"
            onClick={debouncedSaveRecurrences}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </>
  )
}

export default LongTermReservationModalContent
